import type { TrozoDeEmergente } from '@vigilancia/ui/emergentes.js';

/**
 * Lo que las notificaciones dicen con números, fechas y nombres · funciones puras:
 * se prueban sin pantalla.
 */

/** Lo que se ve sobre la campana: nada si no hay, el número hasta nueve, y «9+» desde diez. */
export function cuentaVisible(sinLeer: number | null): string {
  if (sinLeer === null || sinLeer <= 0) return '';
  return sinLeer > 9 ? '9+' : String(sinLeer);
}

const MINUTO = 60;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

/**
 * Hace cuánto, como lo diría una persona —«ahora», «hace 5 minutos», «ayer»— y,
 * desde una semana, la fecha. Lo dice el navegador en el idioma de la pantalla.
 */
export function haceCuanto(momento: string, ahora: Date, idioma: string): string {
  const cuando = new Date(momento);
  const segundos = Math.round((cuando.getTime() - ahora.getTime()) / 1000);
  const lejos = Math.abs(segundos);
  const relativo = new Intl.RelativeTimeFormat(idioma, { numeric: 'auto' });
  if (lejos < MINUTO) return relativo.format(0, 'second');
  if (lejos < HORA) return relativo.format(Math.round(segundos / MINUTO), 'minute');
  if (lejos < DIA) return relativo.format(Math.round(segundos / HORA), 'hour');
  if (lejos < 7 * DIA) return relativo.format(Math.round(segundos / DIA), 'day');
  return new Intl.DateTimeFormat(idioma, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(cuando);
}

/**
 * El día de un momento, para agrupar la página: «hoy», «ayer» y, antes, la fecha con
 * su día de la semana. Se cuenta en días del calendario de quien mira, no en horas.
 */
export function diaDe(momento: string, ahora: Date, idioma: string): string {
  const cuando = new Date(momento);
  const inicio = (fecha: Date): number =>
    new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()).getTime();
  const dias = Math.round((inicio(cuando) - inicio(ahora)) / (DIA * 1000));
  if (dias === 0 || dias === -1) {
    return new Intl.RelativeTimeFormat(idioma, { numeric: 'auto' }).format(dias, 'day');
  }
  return new Intl.DateTimeFormat(idioma, { weekday: 'long', day: 'numeric', month: 'long' }).format(
    cuando,
  );
}

/** Una lista dicha en el idioma de la pantalla: «a, b y c». */
export function enLista(partes: readonly string[], idioma: string): string {
  return new Intl.ListFormat(idioma, { style: 'long', type: 'conjunction' }).format(partes);
}

/** Un trozo de una línea: texto, o texto en negrita —quién, sobre qué—. */
export type Trozo = TrozoDeEmergente;

/** Lo que hace el catálogo: la clave, con sus huecos rellenos. */
export type Traducir = (
  clave: string,
  valores?: Readonly<Record<string, string | number>>,
) => string;

/** Dos caracteres de uso privado: no aparecen en ninguna traducción. */
const ABRE = '\u{F8F0}';
const CIERRA = '\u{F8F1}';
const MARCA = /\u{F8F0}(\d+)\u{F8F1}/u;

/**
 * Un mensaje del catálogo, por trozos, con los valores de `fuertes` en negrita.
 *
 * El catálogo manda en la gramática —dónde va cada nombre, la concordancia—, y por
 * eso no se trocea el texto a mano: se traduce con una marca en el lugar de cada
 * valor y se parte por las marcas. **El valor se queda como dato, nunca como
 * marcado**: lo que lo pinta lo pone como texto.
 */
export function enTrozos(
  t: Traducir,
  clave: string,
  fuertes: Readonly<Record<string, string>>,
  planos: Readonly<Record<string, string | number>> = {},
): Trozo[] {
  const nombres = Object.keys(fuertes);
  const marcas = Object.fromEntries(
    nombres.map((nombre, i) => [nombre, `${ABRE}${String(i)}${CIERRA}`]),
  );
  const partes = t(clave, { ...planos, ...marcas }).split(new RegExp(MARCA.source, 'gu'));
  const trozos: Trozo[] = [];
  partes.forEach((parte, i) => {
    if (i % 2 === 1) {
      const nombre = nombres[Number(parte)];
      const valor = nombre === undefined ? undefined : fuertes[nombre];
      if (valor !== undefined) trozos.push({ fuerte: valor });
    } else if (parte !== '') {
      trozos.push(parte);
    }
  });
  return trozos;
}

/** La línea entera, como texto: para un nombre accesible o un anuncio. */
export function textoDe(trozos: readonly Trozo[]): string {
  return trozos.map((trozo) => (typeof trozo === 'string' ? trozo : trozo.fuerte)).join('');
}
